<template>
  <div class="admin-discounts">
    <div class="admin-header">
      <h1><i class="fa-solid fa-tag"></i> Discount Management</h1>
      <p class="subtitle">Apply 24-hour discounts to products</p>
    </div>

    <!-- Main Content -->
    <div>
      <!-- Filters Section -->
      <div class="filters-section">
        <div class="search-box">
          <i class="fa-solid fa-search"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search products..."
            @input="debouncedSearch"
          />
        </div>
        
        <div class="filter-controls">
          <div class="filter-group">
            <label><i class="fa-solid fa-filter"></i> Filter:</label>
            <select v-model="selectedFilter" @change="fetchProducts">
              <option value="all">All Products</option>
              <option value="discounted">With Discount</option>
              <option value="not-discounted">Without Discount</option>
              <option value="in-stock">In Stock</option>
              <option value="low-stock">Low Stock</option>
            </select>
          </div>
          
          <div class="filter-group">
            <label><i class="fa-solid fa-sort"></i> Sort:</label>
            <select v-model="sortBy" @change="fetchProducts">
              <option value="name">Name A-Z</option>
              <option value="price_high">Price High-Low</option>
              <option value="price_low">Price Low-High</option>
              <option value="discount">Discount %</option>
            </select>
          </div>

          <button class="btn btn-refresh" @click="fetchProducts">
            <i class="fa-solid fa-rotate"></i> Refresh
          </button>
          
          <!-- Quick Select All Button -->
          <button 
            class="btn btn-select-all" 
            @click="toggleSelectAll"
            v-if="filteredProducts.length > 0"
          >
            <i class="fa-solid" :class="allSelected ? 'fa-square-check' : 'fa-square'"></i>
            {{ allSelected ? 'Deselect All' : 'Select All' }}
          </button>
        </div>

        <!-- Search Filters -->
        <div v-if="showAdvancedFilters" class="advanced-filters">
          <div class="filter-row">
            <div class="filter-group">
              <label>Category:</label>
              <select v-model="categoryFilter" @change="fetchProducts">
                <option value="">All Categories</option>
                <option value="football">Football</option>
                <option value="basketball">Basketball</option>
                <option value="fitness">Fitness</option>
                <option value="running">Running</option>
                <option value="swimming">Swimming</option>
                <option value="cycling">Cycling</option>
                <option value="tennis">Tennis</option>
                <option value="cricket">Cricket</option>
                <option value="golf">Golf</option>
              </select>
            </div>
            
            <div class="filter-group">
              <label>Brand:</label>
              <input 
                type="text" 
                v-model="brandFilter" 
                placeholder="Filter by brand"
                @input="debouncedSearch"
              />
            </div>

            <div class="filter-group">
              <label>Price Range:</label>
              <div class="price-range">
                <input type="number" v-model="minPrice" placeholder="Min" @change="fetchProducts" />
                <span>to</span>
                <input type="number" v-model="maxPrice" placeholder="Max" @change="fetchProducts" />
              </div>
            </div>
          </div>
        </div>

        <button class="btn-toggle-filters" @click="toggleAdvancedFilters">
          <i class="fa-solid" :class="showAdvancedFilters ? 'fa-filter-circle-xmark' : 'fa-filter'"></i>
          {{ showAdvancedFilters ? 'Hide Filters' : 'More Filters' }}
        </button>
      </div>

      <!-- Bulk Actions -->
      <div class="bulk-actions" v-if="selectedProducts.length > 0">
        <div class="selection-info">
          <i class="fa-solid fa-check-circle"></i>
          {{ selectedProducts.length }} products selected
          <span class="selected-value">(KES {{ formatPrice(totalSelectedValue) }})</span>
        </div>
        <div class="bulk-buttons">
          <button class="btn btn-bulk" @click="openBulkModal">
            <i class="fa-solid fa-bullhorn"></i> Apply Bulk Discount
          </button>
          <button class="btn btn-remove" @click="removeBulkDiscount">
            <i class="fa-solid fa-trash"></i> Remove All Discounts
          </button>
          <button class="btn btn-clear" @click="clearSelection">
            <i class="fa-solid fa-times"></i> Clear
          </button>
        </div>
      </div>

      <!-- Products Grid -->
      <div v-if="filteredProducts.length > 0" class="products-container">
        <div class="grid-header">
          <div class="grid-stats">
            Showing {{ filteredProducts.length }} products
            <span v-if="selectedFilter !== 'all'" class="filter-badge">
              {{ selectedFilter.replace('-', ' ') }}
            </span>
          </div>
          <div class="grid-pagination">
            <button 
              class="btn-pagination" 
              :disabled="currentPage === 1" 
              @click="changePage(currentPage - 1)"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
            <button 
              class="btn-pagination" 
              :disabled="currentPage === totalPages" 
              @click="changePage(currentPage + 1)"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <div class="products-grid">
          <div 
            v-for="product in paginatedProducts" 
            :key="product.item_id"
            class="product-card"
            :class="{ 
              'discounted': product.discounted,
              'selected': isSelected(product),
              'low-stock': product.stock_quantity <= 5 && product.stock_quantity > 0,
              'out-of-stock': product.stock_quantity === 0
            }"
          >
            <div class="checkbox-wrapper" @click="toggleSelect(product)">
              <input 
                type="checkbox" 
                :checked="isSelected(product)"
                @change="toggleSelect(product)"
                @click.stop
              />
            </div>

            <div class="product-image" @click="openDiscountModal(product)">
              <img
                :src="product.thumbnail || defaultImage"
                :alt="product.name"
                loading="lazy"
                decoding="async"
              />
              <div v-if="product.discounted" class="discount-badge">
                {{ product.discount_price }}% OFF
              </div>
              <div v-if="product.stock_quantity === 0" class="stock-badge out">
                Out of Stock
              </div>
              <div v-else-if="product.stock_quantity <= 5" class="stock-badge low">
                Low Stock
              </div>
            </div>

            <div class="product-info">
              <h3 class="product-name" @click="openDiscountModal(product)">{{ product.name }}</h3>
              <div class="product-meta">
                <span class="brand">{{ product.brand }}</span>
                <span class="category">{{ product.category }}</span>
              </div>
              
              <div class="price-section">
                <div class="price-display">
                  <span class="current-price">KES {{ formatPrice(calculateDiscountedPrice(product)) }}</span>
                  <span v-if="product.discount_price" class="original-price">
                    KES {{ formatPrice(product.price) }}
                  </span>
                </div>
                <div v-if="product.discounted" class="discount-time">
                  <i class="fa-regular fa-clock"></i>
                  <small>24h discount</small>
                </div>
              </div>

              <div class="stock-info">
                <span class="stock-label">Stock:</span>
                <div class="stock-meter">
                  <div class="stock-fill" :style="{ width: `${Math.min((product.stock_quantity / 50) * 100, 100)}%` }"></div>
                </div>
                <span class="stock-quantity">{{ product.stock_quantity }}</span>
              </div>

              <div class="card-actions">
                <button 
                  class="btn btn-edit"
                  @click="openDiscountModal(product)"
                  :disabled="product.stock_quantity === 0"
                >
                  <i class="fa-solid fa-percent"></i>
                  {{ product.discounted ? 'Edit' : 'Discount' }}
                </button>
                <button 
                  v-if="product.discounted"
                  class="btn btn-remove"
                  @click="removeDiscount(product)"
                >
                  <i class="fa-solid fa-trash"></i> Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <i class="fa-solid fa-box-open"></i>
        <h3>No products found</h3>
        <p v-if="searchQuery || selectedFilter !== 'all'">Try adjusting your search or filters</p>
        <button class="btn btn-secondary" @click="resetFilters">
          Reset Filters
        </button>
      </div>
    </div>

    <!-- Individual Discount Modal -->
    <div v-if="showDiscountModal" class="modal-overlay" @click.self="closeDiscountModal">
      <div class="modal-content compact-modal">
        <div class="modal-header">
          <h2>
            <i class="fa-solid fa-percent"></i>
            {{ editingProduct.discounted ? 'Edit Discount' : 'Apply Discount' }}
          </h2>
          <button class="close-btn" @click="closeDiscountModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="product-preview">
            <img
              :src="editingProduct.thumbnail || defaultImage"
              :alt="editingProduct.name"
              decoding="async"
            />
            <div class="preview-info">
              <h3>{{ editingProduct.name }}</h3>
              <p class="brand">{{ editingProduct.brand }}</p>
            </div>
          </div>

          <div class="compact-form">
            <div class="form-group">
              <label>Discount Percentage</label>
              <div class="compact-input-group">
                <div class="percentage-display">
                  <input
                    type="range"
                    v-model.number="discountForm.value"
                    min="0"
                    max="100"
                    step="5"
                    @input="updateDiscountInput"
                  />
                  <span class="percentage-value">{{ discountForm.value || 0 }}%</span>
                </div>
                <div class="quick-percentages">
                  <button 
                    type="button" 
                    class="quick-btn" 
                    v-for="percent in [10, 20, 30, 50]"
                    :key="percent"
                    @click="discountForm.value = percent"
                    :class="{ active: discountForm.value === percent }"
                  >
                    {{ percent }}%
                  </button>
                </div>
              </div>
            </div>

            <div class="price-summary compact">
              <div class="summary-row">
                <span>Original Price:</span>
                <span class="original">KES {{ formatPrice(editingProduct.price) }}</span>
              </div>
              <div class="summary-row">
                <span>Discount:</span>
                <span class="discount">{{ discountForm.value || 0 }}%</span>
              </div>
              <div class="summary-row total">
                <span>New Price:</span>
                <span class="final">KES {{ formatPrice(calculateNewPrice(editingProduct)) }}</span>
              </div>
            </div>

            <div class="time-notice compact">
              <i class="fa-regular fa-clock"></i>
              <span>Active for 24 hours</span>
            </div>

            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closeDiscountModal">
                Cancel
              </button>
              <button 
                type="button" 
                class="btn btn-primary" 
                @click="applyDiscount"
                :disabled="!discountForm.value || applyingDiscount"
              >
                <i class="fa-solid" :class="applyingDiscount ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                {{ applyingDiscount ? 'Applying...' : 'Apply' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Discount Modal -->
    <div v-if="showBulkModal" class="modal-overlay" @click.self="closeBulkModal">
      <div class="modal-content compact-modal">
        <div class="modal-header">
          <h2>
            <i class="fa-solid fa-bullhorn"></i>
            Apply Bulk Discount
          </h2>
          <button class="close-btn" @click="closeBulkModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="selection-summary compact">
            <div class="selected-count">
              <i class="fa-solid fa-layer-group"></i>
              <span>{{ selectedProducts.length }} Products</span>
            </div>
            <div class="selected-value">
              <span>Total Value: KES {{ formatPrice(totalSelectedValue) }}</span>
            </div>
          </div>

          <div class="compact-form">
            <div class="form-group">
              <label>Discount Percentage</label>
              <div class="compact-input-group">
                <div class="percentage-display">
                  <input
                    type="range"
                    v-model.number="bulkDiscountForm.value"
                    min="0"
                    max="100"
                    step="5"
                    @input="updateBulkDiscountInput"
                  />
                  <span class="percentage-value">{{ bulkDiscountForm.value || 0 }}%</span>
                </div>
                <div class="quick-percentages">
                  <button 
                    type="button" 
                    class="quick-btn" 
                    v-for="percent in [10, 20, 30, 50]"
                    :key="percent"
                    @click="bulkDiscountForm.value = percent"
                    :class="{ active: bulkDiscountForm.value === percent }"
                  >
                    {{ percent }}%
                  </button>
                </div>
              </div>
            </div>

            <div class="time-notice compact">
              <i class="fa-regular fa-clock"></i>
              <span>24-hour discount for all selected</span>
            </div>

            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closeBulkModal">
                Cancel
              </button>
              <button 
                type="button" 
                class="btn btn-primary" 
                @click="applyBulkDiscount"
                :disabled="!bulkDiscountForm.value || applyingDiscount"
              >
                <i class="fa-solid" :class="applyingDiscount ? 'fa-spinner fa-spin' : 'fa-bullhorn'"></i>
                {{ applyingDiscount ? 'Applying...' : 'Apply to All' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Remove Single -->
    <div v-if="showConfirmRemove" class="modal-overlay" @click.self="cancelConfirmRemove">
      <div class="modal-content confirm-modal">
        <div class="modal-header">
          <h2><i class="fa-solid fa-trash"></i> Remove discount</h2>
          <button class="close-btn" @click="cancelConfirmRemove"><i class="fa-solid fa-times"></i></button>
        </div>
        <div class="modal-body">
          <p>Remove discount from <strong>{{ confirmRemoveProduct?.name }}</strong>?</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="cancelConfirmRemove">Cancel</button>
            <button type="button" class="btn btn-remove" @click="confirmRemoveSingle">
              <i class="fa-solid fa-trash"></i> Remove
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Remove Bulk -->
    <div v-if="showConfirmBulkRemove" class="modal-overlay" @click.self="cancelConfirmBulkRemove">
      <div class="modal-content confirm-modal">
        <div class="modal-header">
          <h2><i class="fa-solid fa-trash"></i> Remove discounts</h2>
          <button class="close-btn" @click="cancelConfirmBulkRemove"><i class="fa-solid fa-times"></i></button>
        </div>
        <div class="modal-body">
          <p>Remove discounts from <strong>{{ confirmBulkCount }}</strong> selected product(s)?</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="cancelConfirmBulkRemove">Cancel</button>
            <button type="button" class="btn btn-remove" @click="confirmBulkRemove">
              <i class="fa-solid fa-trash"></i> Remove all
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import globalToastMixin from "@/mixins/globalToastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: 'DiscountManager',
  loadingMessage: 'Loading products...',
  mixins: [globalToastMixin, globalLoadingMixin],

  data() {
    return {
      // Products
      products: [],
      filteredProducts: [],
      loading: false,
      currentPage: 1,
      itemsPerPage: 12,
      totalProducts: 0,
      
      // Filters
      searchQuery: '',
      selectedFilter: 'all',
      sortBy: 'name',
      categoryFilter: '',
      brandFilter: '',
      minPrice: null,
      maxPrice: null,
      showAdvancedFilters: false,
      
      // Selection
      selectedProducts: [],
      
      // Modals
      showDiscountModal: false,
      showBulkModal: false,
      showConfirmRemove: false,
      confirmRemoveProduct: null,
      showConfirmBulkRemove: false,
      confirmBulkCount: 0,
      pendingBulkRemove: null,
      
      // Editing
      editingProduct: null,
      discountForm: {
        value: null
      },
      bulkDiscountForm: {
        value: null
      },
      
      // Loading states
      applyingDiscount: false,
      
      // Defaults
      defaultImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iI2NjYyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg',
      
      // Debounce
      searchTimeout: null
    };
  },
  
  computed: {
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProducts.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    },
    
    totalSelectedValue() {
      return this.selectedProducts.reduce((sum, p) => sum + p.price, 0);
    },
    
    allSelected() {
      if (this.filteredProducts.length === 0) return false;
      return this.filteredProducts.every(p => this.isSelected(p));
    }
  },
  
  methods: {
    // API Calls
    async fetchProducts() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.searchQuery) params.append('q', this.searchQuery);
        if (this.selectedFilter === 'discounted') params.append('discounted', 'true');
        if (this.selectedFilter === 'not-discounted') params.append('discounted', 'false');
        if (this.selectedFilter === 'in-stock') params.append('in_stock', 'true');
        if (this.categoryFilter) params.append('category', this.categoryFilter);
        if (this.brandFilter) params.append('brand', this.brandFilter);
        if (this.minPrice !== null) params.append('min_price', this.minPrice);
        if (this.maxPrice !== null) params.append('max_price', this.maxPrice);
        params.append('limit', 100);
        
        const response = await fetch(buildApiUrl(`/admin/products/search?${params.toString()}`));
        
        if (!response.ok) throw new Error('Failed to fetch products');
        
        const data = await response.json();
        this.products = data.products.map(product => ({
          ...product,
          price: parseFloat(product.price),
          discount_price: product.discount_price ? parseFloat(product.discount_price) : null,
          stock_quantity: parseInt(product.stock_quantity) || 0,
          discounted: product.discounted || false
        }));
        
        this.totalProducts = data.count;
        this.applyFilters();
        this.currentPage = 1;
        
      } catch (error) {
        console.error('Error fetching products:', error);
        this.showToastMessage('Error', 'Failed to load products', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async applyDiscount() {
      this.applyingDiscount = true;
      try {
        const payload = {
          product_id: this.editingProduct.item_id,
          discount_value: this.discountForm.value
        };
        
        const response = await fetch(buildApiUrl("/admin/discounts/apply"), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminAccessToken') || localStorage.getItem('admin_token')}`
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) throw new Error('Failed to apply discount');
        
        //const data = await response.json();
        
        // Update local state
        const index = this.products.findIndex(p => p.item_id === this.editingProduct.item_id);
        if (index !== -1) {
          this.products[index] = {
            ...this.products[index],
            discount_price: this.discountForm.value,
            discounted: true
          };
        }
        
        this.showToastMessage(
          'Success',
          `Discount applied to ${this.editingProduct.name}`,
          'success'
        );
        
        this.closeDiscountModal();
        this.applyFilters();
        
      } catch (error) {
        console.error('Error applying discount:', error);
        this.showToastMessage('Error', 'Failed to apply discount', 'error');
      } finally {
        this.applyingDiscount = false;
      }
    },
    
    async applyBulkDiscount() {
      this.applyingDiscount = true;
      try {
        const productIds = this.selectedProducts.map(p => p.item_id);
        const payload = {
          product_ids: productIds,
          discount_value: this.bulkDiscountForm.value
        };
        
        const response = await fetch(buildApiUrl("/admin/discounts/bulk_apply"), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminAccessToken') || localStorage.getItem('admin_token')}`
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) throw new Error('Failed to apply bulk discount');
        
        const data = await response.json();
        
        // Update local state
        productIds.forEach(productId => {
          const index = this.products.findIndex(p => p.item_id === productId);
          if (index !== -1) {
            this.products[index] = {
              ...this.products[index],
              discount_price: this.bulkDiscountForm.value,
              discounted: true
            };
          }
        });
        
        this.showToastMessage(
          'Success',
          `Discount applied to ${data.updated_count} products`,
          'success'
        );
        
        this.closeBulkModal();
        this.clearSelection();
        this.applyFilters();
        
      } catch (error) {
        console.error('Error applying bulk discount:', error);
        this.showToastMessage('Error', 'Failed to apply bulk discount', 'error');
      } finally {
        this.applyingDiscount = false;
      }
    },
    
    async removeDiscount(product) {
      if (!confirm(`Remove discount from ${product.name}?`)) return;
      
      try {
        const response = await fetch(buildApiUrl(`/admin/discounts/remove/${product.item_id}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminAccessToken') || localStorage.getItem('admin_token')}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to remove discount');
        
        // Update local state
        const index = this.products.findIndex(p => p.item_id === product.item_id);
        if (index !== -1) {
          this.products[index] = {
            ...this.products[index],
            discount_price: null,
            discounted: false
          };
        }
        
        this.showToastMessage('Success', `Discount removed from ${product.name}`, 'success');
        this.applyFilters();
        
      } catch (error) {
        console.error('Error removing discount:', error);
        this.showToastMessage('Error', 'Failed to remove discount', 'error');
      }
    },
    
    async removeBulkDiscount() {
      const toRemove = this.selectedProducts.filter(p => p.discounted);
      if (toRemove.length === 0) {
        this.showToastMessage('Info', 'No discounted products selected', 'info');
        return;
      }
      if (!confirm(`Remove discounts from ${toRemove.length} selected product(s)?`)) return;

      try {
        const productIds = toRemove.map(p => p.item_id);
        const token = localStorage.getItem('adminAccessToken') || localStorage.getItem('admin_token');
        const response = await fetch(buildApiUrl("/admin/discounts/bulk_remove"), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ product_ids: productIds })
        });

        if (!response.ok) throw new Error('Failed to remove bulk discounts');

        const data = await response.json();

        toRemove.forEach(p => {
          const index = this.products.findIndex(prod => prod.item_id === p.item_id);
          if (index !== -1) {
            this.products[index] = {
              ...this.products[index],
              discount_price: null,
              discounted: false
            };
          }
        });

        this.showToastMessage(
          'Success',
          data.warning ? `${data.message} (${data.warning})` : data.message,
          'success'
        );
        this.clearSelection();
        this.applyFilters();
      } catch (error) {
        console.error('Error removing bulk discounts:', error);
        this.showToastMessage('Error', 'Failed to remove bulk discounts', 'error');
      }
    },
    
    // Client-side filtering
    applyFilters() {
      let filtered = [...this.products];
      
      // Apply low stock filter (client-side only)
      if (this.selectedFilter === 'low-stock') {
        filtered = filtered.filter(p => p.stock_quantity <= 5 && p.stock_quantity > 0);
      }
      
      // Apply sorting
      this.sortProducts(filtered);
      this.filteredProducts = filtered;
      this.currentPage = 1;
    },
    
    sortProducts(products = null) {
      const list = products || this.filteredProducts;
      
      switch (this.sortBy) {
        case 'name':
          list.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'price_high':
          list.sort((a, b) => b.price - a.price);
          break;
        case 'price_low':
          list.sort((a, b) => a.price - b.price);
          break;
        case 'discount':
          list.sort((a, b) => (b.discount_price || 0) - (a.discount_price || 0));
          break;
      }
      
      if (!products) {
        this.filteredProducts = list;
      }
    },
    
    // Utility methods
    calculateDiscountedPrice(product) {
      if (product.discount_price) {
        return product.price * (1 - product.discount_price / 100);
      }
      return product.price;
    },
    
    calculateNewPrice(product) {
      if (!this.discountForm.value) return product.price;
      return product.price * (1 - this.discountForm.value / 100);
    },
    
    formatPrice(price) {
      return new Intl.NumberFormat('en-KE').format(Math.round(price));
    },
    
    // Selection methods
    isSelected(product) {
      return this.selectedProducts.some(p => p.item_id === product.item_id);
    },
    
    toggleSelect(product) {
      const index = this.selectedProducts.findIndex(p => p.item_id === product.item_id);
      if (index === -1) {
        this.selectedProducts.push(product);
      } else {
        this.selectedProducts.splice(index, 1);
      }
    },
    
    toggleSelectAll() {
      if (this.allSelected) {
        // Deselect all
        this.selectedProducts = this.selectedProducts.filter(
          selected => !this.filteredProducts.some(p => p.item_id === selected.item_id)
        );
      } else {
        // Select all current page items
        //const currentPageIds = this.paginatedProducts.map(p => p.item_id);
        const newSelections = this.paginatedProducts.filter(
          p => !this.selectedProducts.some(selected => selected.item_id === p.item_id)
        );
        this.selectedProducts = [...this.selectedProducts, ...newSelections];
      }
    },
    
    clearSelection() {
      this.selectedProducts = [];
    },
    
    // Modal methods
    openDiscountModal(product) {
      this.editingProduct = { ...product };
      this.discountForm.value = product.discount_price || null;
      this.showDiscountModal = true;
    },
    
    closeDiscountModal() {
      this.showDiscountModal = false;
      this.editingProduct = null;
      this.discountForm.value = null;
    },
    
    updateDiscountInput() {
      if (this.discountForm.value < 0) this.discountForm.value = 0;
      if (this.discountForm.value > 100) this.discountForm.value = 100;
    },
    
    openBulkModal() {
      this.bulkDiscountForm.value = null;
      this.showBulkModal = true;
    },
    
    closeBulkModal() {
      this.showBulkModal = false;
    },
    
    updateBulkDiscountInput() {
      if (this.bulkDiscountForm.value < 0) this.bulkDiscountForm.value = 0;
      if (this.bulkDiscountForm.value > 100) this.bulkDiscountForm.value = 100;
    },
    
    // Search with debounce
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchProducts();
      }, 500);
    },
    
    // Reset
    resetFilters() {
      this.searchQuery = '';
      this.selectedFilter = 'all';
      this.sortBy = 'name';
      this.categoryFilter = '';
      this.brandFilter = '';
      this.minPrice = null;
      this.maxPrice = null;
      this.fetchProducts();
    },
    
    toggleAdvancedFilters() {
      this.showAdvancedFilters = !this.showAdvancedFilters;
    },
    
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
  },
  
  mounted() {
    this.fetchProducts();
    
    // Add keyboard shortcuts
    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.key === 'k') {
        e.preventDefault();
        document.querySelector('.search-box input')?.focus();
      }
      if (e.key === 'Escape') {
        this.closeDiscountModal();
        this.closeBulkModal();
      }
      if (e.ctrlKey && e.key === 'a' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        this.toggleSelectAll();
      }
    });
  }
};
</script>

<style scoped>
.admin-discounts {
  padding: 1.5rem;
  min-height: 100vh;
  background: var(--background-color1);
}

.admin-header {
  margin-bottom: 2rem;
  text-align: center;
  padding: 1.5rem;
  background: var(--background-color2);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--box-shadow);
  border: 1px solid var(--border-color-hover1);
}

.admin-header h1 {
  color: var(--text-color1);
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.subtitle {
  color: var(--text-color2);
  font-size: 0.95rem;
}

/* Filters */
.filters-section {
  background: var(--background-color2);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px var(--box-shadow);
  border: 1px solid var(--border-color-hover1);
}

.search-box {
  position: relative;
  margin-bottom: 1rem;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color-hover1);
  border-radius: 8px;
  background: var(--background-color3);
  color: var(--text-color1);
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.search-box input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 2px var(--border-color-hover1);
}

.filter-controls {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 200px;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.filter-group select {
  flex: 1;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--border-color-hover1);
  border-radius: 8px;
  background: var(--background-color3);
  color: var(--text-color1);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-group select:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.btn {
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-refresh {
  background: var(--background-color3);
  color: var(--text-color1);
  border: 1px solid var(--border-color-hover1);
}

.btn-refresh:hover {
  background: var(--border-color-hover1);
}

.btn-select-all {
  background: var(--background-color3);
  color: var(--text-color1);
  border: 1px solid var(--border-color-hover1);
}

.btn-select-all:hover {
  background: var(--border-color-hover1);
  color: var(--border-color-hover);
}

/* Advanced Filters */
.advanced-filters {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color-hover1);
}

.filter-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-row .filter-group {
  flex: 1;
  min-width: 200px;
}

.filter-row input[type="number"] {
  width: 80px;
  padding: 0.5rem;
  border: 1px solid var(--border-color-hover1);
  border-radius: 6px;
  background: var(--background-color3);
  color: var(--text-color1);
}

.price-range {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-toggle-filters {
  width: 100%;
  margin-top: 1rem;
  padding: 0.75rem;
  background: transparent;
  border: 1px dashed var(--border-color-hover1);
  color: var(--text-color2);
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-toggle-filters:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

/* Bulk Actions */
.bulk-actions {
  background: linear-gradient(135deg, var(--border-color-hover1), rgba(0, 255, 255, 0.1));
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  border: 1px solid var(--border-color-hover);
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.selection-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--border-color-hover);
  font-weight: 600;
  font-size: 0.95rem;
}

.selected-value {
  color: var(--text-color2);
  font-size: 0.85rem;
  margin-left: 0.5rem;
}

.bulk-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn-bulk {
  background: var(--border-color-hover);
  color: var(--button-color);
}

.btn-bulk:hover {
  background: var(--border-color-hover3);
  transform: translateY(-2px);
}

.btn-remove {
  background: transparent;
  border: 1px solid #ff4444;
  color: #ff4444;
}

.btn-remove:hover {
  background: #ff4444;
  color: white;
}

.btn-clear {
  background: transparent;
  border: 1px solid var(--text-color2);
  color: var(--text-color2);
}

.btn-clear:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

/* Products Grid */
.products-container {
  margin-top: 1.5rem;
}

.grid-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: var(--background-color2);
  border-radius: 8px;
  border: 1px solid var(--border-color-hover1);
}

.grid-stats {
  color: var(--text-color1);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.filter-badge {
  background: var(--border-color-hover);
  color: var(--text-color3);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.grid-pagination {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-pagination {
  width: 36px;
  height: 36px;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color3);
  color: var(--text-color1);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--border-color-hover1);
}

.btn-pagination:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  color: var(--text-color2);
  font-size: 0.9rem;
  min-width: 100px;
  text-align: center;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.product-card {
  background: var(--background-color2);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--box-shadow);
  transition: all 0.3s ease;
  border: 1px solid transparent;
  position: relative;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px var(--box-shadow);
  border-color: var(--border-color-hover1);
}

.product-card.selected {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 2px var(--border-color-hover1);
}

.product-card.discounted {
  border-left: 3px solid var(--border-color-hover3);
}

.product-card.low-stock {
  border-left: 3px solid orange;
}

.product-card.out-of-stock {
  border-left: 3px solid #ff4444;
}

.checkbox-wrapper {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 2;
  cursor: pointer;
}

.checkbox-wrapper input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--border-color-hover);
  margin: 0;
}

.product-image {
  position: relative;
  height: 160px;
  overflow: hidden;
  background: var(--background-color3);
  cursor: pointer;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.product-card:hover .product-image img {
  transform: scale(1.05);
}

.discount-badge {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: var(--border-color-hover3);
  color: var(--text-color3);
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.8rem;
}

.stock-badge {
  position: absolute;
  top: 0.75rem;
  right: 2.5rem;
  padding: 0.35rem 0.6rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.75rem;
}

.stock-badge.out {
  background: #ff4444;
  color: white;
}

.stock-badge.low {
  background: orange;
  color: white;
}

.product-info {
  padding: 1rem;
}

.product-name {
  margin: 0 0 0.5rem 0;
  color: var(--text-color1);
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  height: 2.8rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: pointer;
}

.product-name:hover {
  color: var(--border-color-hover);
}

.product-meta {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: var(--text-color2);
  font-size: 0.85rem;
}

.price-section {
  margin-bottom: 0.75rem;
}

.price-display {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.current-price {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--border-color-hover);
}

.original-price {
  font-size: 0.9rem;
  color: var(--text-color2);
  text-decoration: line-through;
}

.discount-time {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--border-color-hover3);
  font-size: 0.8rem;
}

.stock-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.75rem 0;
  font-size: 0.9rem;
}

.stock-label {
  color: var(--text-color2);
  min-width: 45px;
}

.stock-meter {
  flex: 1;
  height: 6px;
  background: var(--background-color3);
  border-radius: 3px;
  overflow: hidden;
}

.stock-fill {
  height: 100%;
  background: var(--border-color-hover);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.product-card.out-of-stock .stock-fill {
  background: #ff4444;
}

.product-card.low-stock .stock-fill {
  background: orange;
}

.stock-quantity {
  color: var(--text-color1);
  font-weight: 600;
  min-width: 40px;
  text-align: right;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.btn-edit {
  flex: 1;
  background: var(--border-color-hover1);
  color: var(--text-color1);
  padding: 0.5rem;
  border-radius: 6px;
  font-size: 0.85rem;
}

.btn-edit:hover:not(:disabled) {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.btn-edit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  background: var(--background-color2);
  border-radius: 12px;
  border: 2px dashed var(--border-color-hover1);
  margin-top: 2rem;
}

.empty-state i {
  font-size: 3rem;
  color: var(--text-color2);
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h3 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color1);
}

.empty-state p {
  color: var(--text-color2);
  margin-bottom: 1rem;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  min-height: 400px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid var(--border-color-hover1);
  border-top: 4px solid var(--border-color-hover);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Compact Modals */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
}

.compact-modal {
  width: 100%;
  max-width: 420px;
  max-height: 70vh;
  overflow-y: auto;
  background: var(--background-color2);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlide 0.3s ease;
  border: 1px solid var(--border-color-hover1);
}

@keyframes modalSlide {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color-hover1);
  background: var(--background-color3);
  position: sticky;
  top: 0;
  z-index: 1;
}

.modal-header h2 {
  margin: 0;
  color: var(--text-color1);
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.close-btn {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: transparent;
  border: none;
  color: var(--text-color2);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: var(--background-color3);
  color: var(--text-color1);
}

.modal-body {
  padding: 1.25rem;
}

/* Product Preview */
.product-preview {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0.75rem;
  background: var(--background-color3);
  border-radius: 8px;
  margin-bottom: 1.25rem;
}

.product-preview img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 2px solid var(--border-color-hover1);
}

.preview-info {
  flex: 1;
}

.preview-info h3 {
  margin: 0 0 0.25rem 0;
  color: var(--text-color1);
  font-size: 1rem;
  font-weight: 600;
}

.preview-info .brand {
  color: var(--text-color2);
  font-size: 0.85rem;
  margin: 0;
}

/* Compact Form */
.compact-form {
  margin-top: 1rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-color1);
  font-weight: 600;
  font-size: 0.95rem;
}

.compact-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.percentage-display {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.percentage-display input[type="range"] {
  flex: 1;
  height: 6px;
  background: var(--background-color3);
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
}

.percentage-display input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  background: var(--border-color-hover);
  border-radius: 50%;
  cursor: pointer;
  border: 3px solid var(--background-color2);
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
}

.percentage-value {
  min-width: 50px;
  text-align: center;
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--border-color-hover);
}

.quick-percentages {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.quick-btn {
  padding: 0.5rem;
  background: var(--background-color3);
  border: 1px solid var(--border-color-hover1);
  border-radius: 6px;
  color: var(--text-color1);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
}

.quick-btn:hover,
.quick-btn.active {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border-color: var(--border-color-hover);
}

/* Compact Price Summary */
.price-summary.compact {
  background: var(--background-color3);
  padding: 0.75rem;
  border-radius: 8px;
  margin: 1rem 0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0;
  color: var(--text-color1);
  font-size: 0.9rem;
}

.summary-row.total {
  border-top: 1px solid var(--border-color-hover1);
  margin-top: 0.375rem;
  padding-top: 0.5rem;
  font-weight: 700;
}

.summary-row .original {
  color: var(--text-color2);
  text-decoration: line-through;
}

.summary-row .discount {
  color: var(--border-color-hover3);
  font-weight: 600;
}

.summary-row .final {
  color: var(--border-color-hover);
  font-size: 1.1rem;
  font-weight: 700;
}

/* Compact Selection Summary */
.selection-summary.compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--background-color3);
  border-radius: 8px;
  margin-bottom: 1.25rem;
}

.selected-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color1);
  font-weight: 600;
  font-size: 0.95rem;
}

.selected-count i {
  color: var(--border-color-hover);
}

.selected-value {
  color: var(--text-color2);
  font-size: 0.9rem;
}

/* Time Notice */
.time-notice.compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(0, 255, 255, 0.1);
  border-radius: 8px;
  border-left: 3px solid var(--border-color-hover);
  margin: 1rem 0;
  color: var(--text-color1);
  font-size: 0.85rem;
}

.time-notice i {
  color: var(--border-color-hover);
}

/* Modal Actions */
.modal-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.btn-secondary {
  flex: 1;
  background: var(--background-color3);
  color: var(--text-color1);
  border: 1px solid var(--border-color-hover1);
  padding: 0.75rem;
}

.btn-secondary:hover {
  background: var(--border-color-hover1);
}

.btn-primary {
  flex: 1;
  background: var(--border-color-hover);
  color: var(--text-color3);
  padding: 0.75rem;
}

.btn-primary:hover:not(:disabled) {
  background: var(--border-color-hover3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Toast */
.toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  min-width: 300px;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 1001;
  animation: slideInRight 0.3s ease;
  background: var(--background-color2);
  border-left: 4px solid var(--border-color-hover);
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast.success {
  border-left-color: var(--border-color-hover3);
}

.toast.error {
  border-left-color: #ff4444;
}

.toast i {
  font-size: 1.5rem;
}

.toast.success i {
  color: var(--border-color-hover3);
}

.toast.error i {
  color: #ff4444;
}

.toast-content {
  flex: 1;
}

.toast-content strong {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--text-color1);
}

.toast-content p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-color2);
}

.toast-close {
  background: transparent;
  border: none;
  color: var(--text-color2);
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.toast-close:hover {
  opacity: 1;
}

/* Responsive Design */
@media (max-width: 768px) {
  .admin-discounts {
    padding: 1rem;
  }
  
  .admin-header h1 {
    font-size: 1.5rem;
  }
  
  .filter-controls {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .filter-group {
    min-width: 100%;
  }
  
  .bulk-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  
  .bulk-buttons {
    flex-direction: column;
  }
  
  .bulk-buttons .btn {
    width: 100%;
    justify-content: center;
  }
  
  .grid-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
  
  .grid-pagination {
    justify-content: center;
  }
  
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
  
  .card-actions {
    flex-direction: column;
  }
  
  .card-actions .btn {
    width: 100%;
  }
  
  .compact-modal {
    margin: 1rem;
    max-height: 80vh;
  }
  
  .quick-percentages {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .products-grid {
    grid-template-columns: 1fr;
  }
  
  .product-card {
    max-width: 100%;
  }
  
  .compact-modal {
    margin: 0.5rem;
  }
  
  .modal-actions {
    flex-direction: column;
  }
}
</style>